/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.keyexchange.KeyExchange
import com.ionspin.kotlin.crypto.keyexchange.KeyExchangeKeyPair
import com.ionspin.kotlin.crypto.keyexchange.crypto_kx_PUBLICKEYBYTES
import com.ionspin.kotlin.crypto.keyexchange.crypto_kx_SECRETKEYBYTES
import kotlinx.serialization.KSerializer
import kotlinx.serialization.SerializationException
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import net.sergeych.crypto2.SafeKeyExchange.SessionKey

/**
 * Safe (and typesafe) key exchange based on the x25519 protocol.
 *
 * Usage:
 *
 * 1. Create [SafeKeyExchange] on both server and client sides
 * 2. Exchange [PublicKey] instances
 * 3. Create [serverSessionKey] and [clientSessionKey] respectively
 * 4. Use [SessionKey.sendingKey] and [SessionKey.receivingKey] to send and receive encrypted data.
 *
 * Some important rules to keep security level high:
 *
 * - do not reuse [SafeKeyExchange] after session keys are generated, at least on the client. Create new
 *   instances as often as performance considerations allow.
 * - while it is possible to generate several keys "ahead", the care should be taken when storing them,
 *   encrypt it with some other key to maintain safety.
 * - do not use [EncryptingPublicKey] for anything but creating session keys.
 * - the serialized form includes the secret exchange key, so it should be encrypted when stored.
 */
@Serializable(with = SafeKeyExchange.SafeKeyExchangeSerializer::class)
class SafeKeyExchange private constructor(
    private val pair: KeyExchangeKeyPair,
) {

    constructor() : this(KeyExchange.keypair())

    @Serializable
    private class Packed(
        val publicKey: UByteArray,
        val secretKey: UByteArray,
    )

    object SafeKeyExchangeSerializer : KSerializer<SafeKeyExchange> {
        private val packedSerializer = Packed.serializer()

        override val descriptor: SerialDescriptor = packedSerializer.descriptor

        override fun serialize(encoder: Encoder, value: SafeKeyExchange) {
            encoder.encodeSerializableValue(
                packedSerializer,
                Packed(value.pair.publicKey, value.pair.secretKey)
            )
        }

        override fun deserialize(decoder: Decoder): SafeKeyExchange {
            val packed = decoder.decodeSerializableValue(packedSerializer)
            require(packed.publicKey.size == crypto_kx_PUBLICKEYBYTES) {
                "SafeKeyExchange public key must be $crypto_kx_PUBLICKEYBYTES bytes"
            }
            require(packed.secretKey.size == crypto_kx_SECRETKEYBYTES) {
                "SafeKeyExchange secret key must be $crypto_kx_SECRETKEYBYTES bytes"
            }
            return SafeKeyExchange(KeyExchangeKeyPair(packed.publicKey, packed.secretKey))
        }
    }

    /**
     * The session key. It uses a pair of keys to encrypt and decrypt messages to maintain high
     * security level and allow using counters as nonce with no extra precautions.
     */
    @Serializable(with = SafeKeyExchange.SessionKeySerializer::class)
    class SessionKey(
        val sendingKey: EncryptingKey,
        val receivingKey: DecryptingKey,
        val isClient: Boolean,
    ) : EncryptingKey by sendingKey, DecryptingKey by receivingKey {

        override val nonceBytesLength: Int = sendingKey.nonceBytesLength

        /**
         * The unique per-session confidential tag, same on both sides.
         * It can't be derived from public keys alone.
         * It is often as a base for authentication tokens and nonce generation as
         * is known immediately at session start and does not need additional data exchange.
         */
        @Suppress("unused")
        val sessionTag: UByteArray by lazy {
            if (!isClient)
                blake2b(id.binaryTag + id.binaryTag)
            else
                blake2b(id.binaryTag + id.binaryTag)
        }

        override val id: KeyId
            get() = if( isClient ) sendingKey.id else receivingKey.id

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is SessionKey) return false

            if (sendingKey != other.sendingKey) return false
            if (receivingKey != other.receivingKey) return false
            if (isClient != other.isClient) return false

            return true
        }

        override fun hashCode(): Int {
            var result = sendingKey.hashCode()
            result = 31 * result + receivingKey.hashCode()
            result = 31 * result + isClient.hashCode()
            return result
        }


    }

    @Serializable
    private class PackedSessionKey(
        val sendingKey: SymmetricKey,
        val receivingKey: SymmetricKey,
        val isClient: Boolean,
    )

    object SessionKeySerializer : KSerializer<SessionKey> {
        private val packedSerializer = PackedSessionKey.serializer()

        override val descriptor: SerialDescriptor = packedSerializer.descriptor

        override fun serialize(encoder: Encoder, value: SessionKey) {
            val sendingKey = value.sendingKey as? SymmetricKey
                ?: throw SerializationException("SessionKey.sendingKey must be SymmetricKey")
            val receivingKey = value.receivingKey as? SymmetricKey
                ?: throw SerializationException("SessionKey.receivingKey must be SymmetricKey")

            encoder.encodeSerializableValue(
                packedSerializer,
                PackedSessionKey(sendingKey, receivingKey, value.isClient)
            )
        }

        override fun deserialize(decoder: Decoder): SessionKey {
            val packed = decoder.decodeSerializableValue(packedSerializer)
            return SessionKey(packed.sendingKey, packed.receivingKey, packed.isClient)
        }
    }

    /**
     * The public key; it should be transmitted to the other party, this is serializable.
     * Do not use it except to get [SessionKey] with [clientSessionKey] or [serverSessionKey]. Storing and reusing
     * it is a great danger.
     *
     * Instances can be compared and used as hashtable keys.
     */
    @Serializable
    class PublicKey(val keyBytes: UByteArray) {
        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other == null || this::class != other::class) return false

            other as PublicKey

            return keyBytes.contentEquals(other.keyBytes)
        }

        override fun hashCode(): Int {
            return keyBytes.contentHashCode()
        }
    }

    /**
     * The public key to be sent to the other party. When received, get the session keys with [clientSessionKey]
     * and [serverSessionKey] respectively. Do not reuse public key, do not use it for any other reason than
     * to create a single session keys instance. For a new session, create new [SafeKeyExchange] at least on the
     * client side, and get new keys. It is recommended to change server-side [SafeKeyExchange]
     * instance every time or often enough.
     */
    val publicKey = PublicKey(pair.publicKey)

    /**
     * Create asymmetric [SessionKey] to encrypt the session. It can't decrypt what is encrypted by it
     * but can decrypt what was encrypted with the [serverSessionKey] on the another side.
     */
    fun clientSessionKey(serverPublicKey: PublicKey): SessionKey =
        KeyExchange.clientSessionKeys(pair.publicKey, pair.secretKey, serverPublicKey.keyBytes)
            .let { SessionKey(SymmetricKey(it.sendKey), SymmetricKey(it.receiveKey), isClient = true) }

    /**
     * Create an asymmetric [SessionKey] instance to work with [clientSessionKey] on the other side.
     * Note that asymmetric means it can't decrypt what it encrypted.
     */
    fun serverSessionKey(clientPublicKey: PublicKey): SessionKey =
        KeyExchange.serverSessionKeys(pair.publicKey, pair.secretKey, clientPublicKey.keyBytes)
            .let { SessionKey(SymmetricKey(it.sendKey), SymmetricKey(it.receiveKey), isClient = false) }


}
